import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest, API_URL } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface GitLabInstallation {
  id: string;
  gitlabUserId: number;
  gitlabUsername: string;
  gitlabEmail: string;
  instanceUrl: string;
  expiresAt: string;
  organizationId: string;
  isActive: boolean;
  installedAt: string;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface GitLabProject {
  id: number;
  name: string;
  pathWithNamespace: string;
  description: string | null;
}

interface GitLabState {
  currentInstallation: GitLabInstallation | null;
  installations: GitLabInstallation[];
  projects: GitLabProject[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: GitLabState = {
  currentInstallation: null,
  installations: [],
  projects: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getInstallations = createAsyncThunk(
  'gitlab/getInstallations',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/gitlab/installations?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getInstallation = createAsyncThunk(
  'gitlab/getInstallation',
  async (installationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/gitlab/installations/${installationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const disconnectGitLab = createAsyncThunk(
  'gitlab/disconnectGitLab',
  async (installationId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/gitlab/installations/${installationId}`, { method: 'DELETE' });
      return installationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getGitLabProjects = createAsyncThunk(
  'gitlab/getGitLabProjects',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/gitlab/oauth/projects?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Initiate GitLab OAuth connection
 * Step 1: Call API with auth to get GitLab OAuth URL
 * Step 2: Redirect browser to GitLab
 */
export const initiateGitLabConnect = createAsyncThunk(
  'gitlab/initiateConnect',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      // Call API with authentication to get GitLab installation URL
      const response = await apiRequest(`/gitlab/install?organizationId=${organizationId}`);
      const installUrl = response.response?.installUrl || response.installUrl;

      if (!installUrl) {
        throw new Error('No installation URL returned from API');
      }

      // Redirect browser to GitLab OAuth
      window.location.href = installUrl;

      return { installUrl };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get installations
  [getInstallations.pending.type]: { status: 'loading' as const },
  [getInstallations.fulfilled.type]: (state: GitLabState, action: any) => {
    // Handle null response (404 - no installations found)
    if (!action.payload) {
      state.installations = [];
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response: { response: [...], metadata: {...} }
    const installations = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.installations || action.payload.installations || [];

    state.installations = installations;
    state.currentInstallation = installations[0] || null;
    state.status = 'success';
    state.error = '';
  },
  [getInstallations.rejected.type]: (state: GitLabState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installations';
  },

  // Get single installation
  [getInstallation.pending.type]: { status: 'loading' as const },
  [getInstallation.fulfilled.type]: (state: GitLabState, action: any) => {
    // Handle null response (404 - installation not found)
    if (!action.payload) {
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend wraps response in { response: {...}, metadata: {...} }
    const installation = action.payload.response || action.payload;
    state.currentInstallation = installation;
    state.status = 'success';
    state.error = '';
  },
  [getInstallation.rejected.type]: (state: GitLabState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installation';
  },

  // Disconnect GitLab
  [disconnectGitLab.pending.type]: { status: 'loading' as const },
  [disconnectGitLab.fulfilled.type]: (state: GitLabState, action: any) => {
    const installationId = action.payload;
    state.installations = state.installations.filter(i => i.id !== installationId);
    if (state.currentInstallation?.id === installationId) {
      state.currentInstallation = null;
    }
    state.status = 'success';
    state.error = '';
  },
  [disconnectGitLab.rejected.type]: (state: GitLabState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to disconnect GitLab';
  },

  // Get GitLab projects
  [getGitLabProjects.pending.type]: { status: 'loading' as const },
  [getGitLabProjects.fulfilled.type]: (state: GitLabState, action: any) => {
    // Handle null response (404 - no projects found)
    if (!action.payload) {
      state.projects = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response
    const projects = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.projects || action.payload.projects || [];

    state.projects = projects;
    state.status = 'success';
    state.error = '';
  },
  [getGitLabProjects.rejected.type]: (state: GitLabState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch GitLab projects';
  },
};

// ==============================|| SLICE ||============================== //

export const gitlabSlice = createSlice({
  name: 'gitlab',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    clearGitLabState(state) {
      state.currentInstallation = null;
      state.installations = [];
      state.projects = [];
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, clearGitLabState } = gitlabSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectCurrentInstallation = (state: any) => state.gitlab.currentInstallation;
export const selectInstallations = (state: any) => state.gitlab.installations;
export const selectProjects = (state: any) => state.gitlab.projects;
export const selectGitLabStatus = (state: any) => state.gitlab.status;
export const selectGitLabError = (state: any) => state.gitlab.error;

// ==============================|| REDUCER ||============================== //

export default gitlabSlice.reducer;
